<template>
	<div class="icon-container" ref="iconContainerElement" @mouseenter="showPopover" @mouseleave="hidePopover">
		<slot name="icon">
			<component :is="iconComponent" class="icon" />
		</slot>

		<div v-show="isPopoverVisible" ref="popoverElement" :style="popoverStyle" class="popover">
			<slot v-if="$slots.default"></slot>
			<span v-else>{{ text }}</span>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { defineProps, ref, onMounted, useSlots, onBeforeUnmount, nextTick } from 'vue';
	import { InformationCircleIcon } from '@heroicons/vue/24/outline';

	const props = defineProps<{
		text?: string;
		icon?: string | object;
	}>();

	const slots = useSlots();

	const iconComponent = ref(props.icon || InformationCircleIcon);
	const isPopoverVisible = ref(false);

	interface PopoverStyle {
		left?: string;
		right?: string;
		bottom?: string;
	}

	const popoverStyle = ref<PopoverStyle>({});
	const iconContainerElement = ref<HTMLElement | null>(null);
	const popoverElement = ref<HTMLElement | null>(null);

	const showPopover = async () => {
		isPopoverVisible.value = true;

		await nextTick();

		adjustPopoverPosition();
	};

	const hidePopover = () => isPopoverVisible.value = false;

	const adjustPopoverPosition = () => {
		const container = iconContainerElement.value;
		const popoverEl = popoverElement.value;

		if (container && popoverEl) {
			const containerRect = container.getBoundingClientRect();
			const popoverRect = popoverEl.getBoundingClientRect();

			// adjust if popover overflows on the right
			if (containerRect.left + popoverRect.width / 2 > window.innerWidth) {
				popoverStyle.value = {
					right: '0'
				};
			}

			// adjust if popover overflows on the left
			if (containerRect.right - popoverRect.width / 2 < 0) {
				popoverStyle.value = {
					left: '0'
				};
			}

			// adjust if popover overflows on the bottom
			if (containerRect.bottom + popoverRect.height > window.innerHeight - 10) {
				popoverStyle.value.bottom = '100%';
			}
		}
	};

	onMounted(() => {
		if (!props.text && !slots.default) {
			console.warn("Popover: Either 'text' prop or default slot content is required.");
		}

		window.addEventListener('resize', adjustPopoverPosition);
	});

	onBeforeUnmount(() => {
		window.removeEventListener('resize', adjustPopoverPosition);
	});
</script>

<style scoped>
	.icon-container {
		position: relative;
		display: inline-block;
		cursor: pointer;
	}

	.icon {
		width: 1rem;
		height: 1rem;
		color: #777;
	}

	.popover {
		position: absolute;
		background-color: white;
		color: black;
		padding: 0.5rem 1rem;
		margin: 0.5rem 0;
		border-radius: 4px;
		white-space: nowrap;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
		opacity: 0;
		transition: opacity 0.2s ease;
		z-index: 200;
		max-width: 90vw;
	}

	.icon-container:hover .popover {
		opacity: 1;
	}
</style>
